import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { FileCheck, ChevronDown, Plus, Calendar, ArrowLeft } from 'lucide-react';
import { getProjects, getTestSets, createTestSet } from '../services/api';
import { formatDistanceToNow } from 'date-fns';
import { useCurrentUser, useApiData, useAsyncOperation, useFormState, useClickOutside } from '../hooks/useApi';

const TestSetsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedProject, setSelectedProject] = useState(null);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const dropdownRef = useRef(null);

  // Use custom hooks for better state management
  const { user, loading: userLoading } = useCurrentUser();
  const { execute: executeCreateTestSet, loading: createLoading } = useAsyncOperation();
  const { formData: newTestSet, updateField, resetForm } = useFormState({
    name: '',
    description: ''
  });

  // Fetch projects data
  const {
    data: projects,
    loading: projectsLoading,
    error: projectsError
  } = useApiData(
    user ? () => getProjects(user.id) : null,
    [user?.id],
    { skip: !user }
  );

  // Fetch test sets for selected project
  const {
    data: testSets,
    loading: testSetsLoading,
    refetch: refetchTestSets
  } = useApiData(
    selectedProject ? () => getTestSets(selectedProject.id) : null,
    [selectedProject?.id],
    { skip: !selectedProject, initialData: [] }
  );

  // Handle dropdown clicks outside
  useClickOutside(dropdownRef, () => setShowProjectDropdown(false));

  useEffect(() => {
    if (projects && projects.length > 0) {
      // Check if there's a project ID in URL params
      const projectIdFromUrl = searchParams.get('project');
      if (projectIdFromUrl) {
        const projectFromUrl = projects.find(p => p.id === projectIdFromUrl);
        if (projectFromUrl) {
          setSelectedProject(projectFromUrl);
        } else {
          setSelectedProject(projects[0]);
        }
      } else {
        setSelectedProject(projects[0]);
      }
    }
  }, [projects, searchParams]);

  useEffect(() => {
    if (selectedProject) {
      // Update URL with selected project
      setSearchParams({ project: selectedProject.id });
    }
  }, [selectedProject, setSearchParams]);

  const handleCreateTestSet = async (e) => {
    e.preventDefault();
    if (!selectedProject) return;
    
    try {
      const testSetData = {
        ...newTestSet,
        projectId: selectedProject.id
      };
      
      await executeCreateTestSet(() => createTestSet(testSetData));
      resetForm();
      setShowCreateForm(false);
      refetchTestSets();
    } catch (error) {
      console.error('Error creating test set:', error);
    }
  };

  const loading = userLoading || projectsLoading;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading test sets...</p>
        </div>
      </div>
    );
  }

  if (projectsError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <FileCheck className="h-12 w-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to load projects</h3>
          <p className="text-gray-500 mb-6">
            There was an error loading your projects. Please refresh the page.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="text-gray-500 hover:text-gray-700 flex items-center"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back to Dashboard
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                    <FileCheck className="h-6 w-6 mr-2" />
                    Test Sets
                  </h1>
                  <p className="mt-1 text-sm text-gray-600">
                    Manage test sets across your projects
                  </p>
                </div>
              </div>
              
              {selectedProject && (
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Test Set
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!projects || projects.length === 0 ? (
          <div className="text-center py-12">
            <FileCheck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No projects yet</h3>
            <p className="text-gray-500 mb-6">
              Create a project first to start managing test sets.
            </p>
            <Link
              to="/dashboard"
              className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors"
            >
              Create Project
            </Link>
          </div>
        ) : (
          <>
            {/* Project Selector */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Project
              </label>
              <div className="relative max-w-md" ref={dropdownRef}>
                <button
                  onClick={() => setShowProjectDropdown(!showProjectDropdown)}
                  className="w-full flex items-center justify-between p-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-gray-900 truncate block">
                      {selectedProject?.name || 'Select Project'}
                    </span>
                    {selectedProject && (
                      <span className="text-xs text-gray-500">
                        {testSets.length} test set{testSets.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${showProjectDropdown ? 'rotate-180' : ''}`} />
                </button>

                {showProjectDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                    {projects.map((project) => (
                      <button
                        key={project.id}
                        onClick={() => {
                          setSelectedProject(project);
                          setShowProjectDropdown(false);
                        }}
                        className={`w-full text-left p-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                          selectedProject?.id === project.id ? 'bg-primary-50 text-primary-700' : 'text-gray-900'
                        }`}
                      >
                        <div className="text-sm font-medium truncate">{project.name}</div>
                        {project.description && (
                          <div className="text-xs text-gray-500 truncate mt-1">{project.description}</div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Test Sets Content */}
            {selectedProject && (
              <div>
                {testSetsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  </div>
                ) : testSets.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                      <FileCheck className="w-12 h-12 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Create your first test set</h3>
                    <p className="text-gray-500 mb-6">
                      Test sets help you evaluate prompt performance with consistent test cases.
                    </p>
                    <button
                      onClick={() => setShowCreateForm(true)}
                      className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors flex items-center mx-auto"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Create Test Set
                    </button>
                  </div>
                ) : (
                  <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {testSets.map((testSet) => (
                      <div
                        key={testSet.id}
                        className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => navigate(`/projects/${selectedProject.id}/test-sets/${testSet.id}`)}
                      >
                        <div className="p-6">
                          <h3 className="text-lg font-medium text-gray-900 mb-2">
                            {testSet.name}
                          </h3>
                          {testSet.description && (
                            <p className="text-gray-600 text-sm mb-4">
                              {testSet.description}
                            </p>
                          )}
                          <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-500">
                              {testSet.test_case_count || 0} test case{(testSet.test_case_count || 0) !== 1 ? 's' : ''}
                            </div>
                            <div className="text-sm text-gray-400 flex items-center">
                              <Calendar className="h-3 w-3 mr-1" />
                              {formatDistanceToNow(new Date(testSet.created_at), { addSuffix: true })}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Test Set Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-lg bg-white rounded-lg shadow-lg">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Create New Test Set
              </h3>
              
              <form onSubmit={handleCreateTestSet} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={newTestSet.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    placeholder="e.g., Customer Support Responses"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={newTestSet.description}
                    onChange={(e) => updateField('description', e.target.value)}
                    placeholder="Describe what this test set evaluates..."
                    rows={3}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                    disabled={createLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                    disabled={createLoading}
                  >
                    {createLoading ? 'Creating...' : 'Create Test Set'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestSetsPage; 